import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { usePropertyStore } from "../contexts/PropertyStoreContext";
import useDeepLinkedProperty, {
  DEEP_LINK_PROPERTY_STATUS,
} from "./useDeepLinkedProperty";

/**
 * Puts the app on the property a deep link names, before anything reads it.
 *
 * A promo is fetched as (promoId, propertyId), so a link opened while a
 * different hotel is selected asks the wrong one — which comes back empty
 * rather than erroring, and shows as a blank drawer or an empty modal.
 *
 * Resolution goes through the user's own property list, which is the access
 * boundary: a link forwarded to someone who does not hold that hotel reports
 * not-found instead of switching them onto it.
 *
 * @param {string|number|null} propertyId the id the link names, already gated by
 *   whatever makes the link active on the calling page — pass null when it is
 *   not. Callers therefore need no second "is this link live" condition.
 * @param {Object} [options]
 * @param {string} [options.notFoundMessage] shown once when the user does not
 *   hold the property. Being told beats staring at an empty drawer.
 * @param {string} [options.searchText] hotel name hint for the property search.
 * @returns {{ isOnDeepLinkedProperty: boolean, status: string }} the flag is
 *   false while a switch is still pending, so callers can hold off until it
 *   lands; status is a DEEP_LINK_PROPERTY_STATUS.
 */
const useDeepLinkedPropertySwitch = (
  propertyId,
  { notFoundMessage, searchText } = {}
) => {
  const { selectedProperty, properties, totalCount, setSelectedProperty } =
    usePropertyStore();

  const { property: deepLinkedProperty, status } = useDeepLinkedProperty(
    propertyId,
    properties,
    totalCount,
    searchText
  );

  const isOnDeepLinkedProperty =
    !propertyId || String(selectedProperty?.propertyId) === String(propertyId);

  // Guarded on the id rather than on object identity so a re-render cannot
  // re-trigger the switch.
  const switchedToRef = useRef(null);
  useEffect(() => {
    if (isOnDeepLinkedProperty || !deepLinkedProperty) return;
    if (switchedToRef.current === propertyId) return;
    switchedToRef.current = propertyId;
    setSelectedProperty(deepLinkedProperty);
  }, [
    isOnDeepLinkedProperty,
    deepLinkedProperty,
    propertyId,
    setSelectedProperty,
  ]);

  // Fires once per id.
  const reportedMissingRef = useRef(null);
  useEffect(() => {
    if (status !== DEEP_LINK_PROPERTY_STATUS.NOT_FOUND) return;
    if (reportedMissingRef.current === propertyId) return;
    reportedMissingRef.current = propertyId;
    if (notFoundMessage) toast.error(notFoundMessage);
  }, [status, propertyId, notFoundMessage]);

  return { isOnDeepLinkedProperty, status };
};

export default useDeepLinkedPropertySwitch;
